import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import _get from 'lodash-es/get';
import { PaymentFormService } from '../payment-form.service';
import { generateGuid } from 'utils/helpers';
import { BehaviorSubject } from 'rxjs';
import { PaymentFormData, StripeEvent } from '../payment-form.interface';
import { PAYMENT_TYPES } from 'services/payment/payment.interface';
import { DEFAULT_CARD_FORM_DATA_VALUE, HolderNameData } from 'features/payment-form/payment-form.constants';
import _cloneDeep from 'lodash-es/cloneDeep';
import { STRIPE_FORM_BASE_STYLES } from 'features/payment-form/stripe-form/stripe-form.constants';

@Component({
  selector: 'cm-stripe-form',
  templateUrl: 'stripe-form.component.html',
  styleUrls: ['stripe-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StripeFormComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Input() showError = false;
  @Output() formChange = new EventEmitter<any>();

  private _holderName: string;
  get holderName(): string {
    return this._holderName;
  }

  set holderName(value: string) {
    this._holderName = value;
    this.holderNameFormData$.next(new HolderNameData(value));
  }

  cardElement;
  readonly cardElementId = `card-elem_${generateGuid()}`;

  holderNameFormData$ = new BehaviorSubject<PaymentFormData<string>>(new HolderNameData(''));
  cardFormData$ = new BehaviorSubject<PaymentFormData<any>>(_cloneDeep(DEFAULT_CARD_FORM_DATA_VALUE));

  private get cardElementSelector() {
    return `#${this.cardElementId}`;
  }

  constructor(
    private paymentService: PaymentFormService,
    private renderer: Renderer2,
    private elem: ElementRef,
  ) {}

  ngOnInit() {
    this.paymentService
      .subscribeOnFormChanges(this.holderNameFormData$, this.cardFormData$, PAYMENT_TYPES.STRIPE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => this.formChange.emit(data));
  }

  ngAfterViewInit() {
    this.initStripe();
    const input = this.elem.nativeElement.querySelector('.__PrivateStripeElement-input');
    if (input) {
      this.renderer.setAttribute(input, 'aria-label', 'PrivateStripeElement');
      this.renderer.removeAttribute(input, 'aria-hidden');
    }
  }

  ngOnDestroy() {
    this.cardElement.unmount();
  }

  initStripe() {
    this.cardElement = this.paymentService.stripeInstance.elements().create('card', {
      hidePostalCode: true,
      style: {
        base: STRIPE_FORM_BASE_STYLES,
      },
      classes: {
        focus: 'is-focused',
      },
    });
    this.cardElement.mount(this.cardElementSelector);
    this.cardElement.on('change', (event: StripeEvent) => {
      this.cardFormData$.next({
        value: this.cardElement,
        valid: event.complete,
        errorMessage: _get(event, 'error.message', ''),
      });
    });
  }
}
